import './field.css';
import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/use-localization';
import { InfoMessage } from './info-message';
import { ErrorMessage } from './error-message';

export type GenericFieldProps = {
    fieldName?: string;
    label?: string;
    required?: boolean;
    errorMessage?: string;
    infoText?: string;
    placeholder?: string;
    inputClassName?: string;
    maxLength?: number;
    type?: string;
    value?: string | number;
    disabled?: boolean;
    inputMode?: string;
    validation?: (value: string) => boolean;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    min?: number | string;
    max?: number | string;
    rows?: number;
    isValidated?: React.Dispatch<React.SetStateAction<boolean>>;
    showRequired?: boolean;
    suffix?: string;
    grouped?: boolean | 'top' | 'middle' | 'bottom' | '';
    validateOnEnter?: boolean;
    showRequiredError?: boolean;
    isActive?: boolean;
    darkMode?: boolean;
};

type ExtendedFieldProps = {
    list: { name: string; email: string }[];
    dropText: string;
};

export type FieldProps = GenericFieldProps & Partial<ExtendedFieldProps>;

export const Field = ({
    fieldName = '',
    required = false,
    infoText = '',
    errorMessage = '',
    placeholder = '',
    label = '',
    maxLength,
    type = 'text',
    value = '',
    disabled = false,
    inputMode = '',
    validation,
    onChange,
    onBlur,
    min,
    max,
    rows = 5,
    inputClassName = '',
    isValidated = undefined,
    showRequired = false,
    suffix = '',
    grouped = false,
    validateOnEnter = false,
    showRequiredError = false,
    isActive = false,
    darkMode = false,
}: FieldProps) => {
    const { localize, getLanguage } = useLocalization();

    const resolvedErrorMessage = errorMessage || localize('input-field-default-error');

    const [touched, setTouched] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const fieldRef = useRef<HTMLInputElement>(null);
    const [enterIsPressed, setEnterIsPressed] = useState(false);
    const [hasTriggeredValidation, setHasTriggeredValidation] = useState(false);
    const htmlName = fieldName ? fieldName?.replace(/[^a-z0-9-_]+/g, '').toLocaleLowerCase() : useId();

    useEffect(() => {
        if (!validation) {
            return;
        }

        try {
            const isValid = validation(`${value}`);
            isValidated?.(isValid);

            // Reset triggered state when value becomes valid
            if (isValid && hasTriggeredValidation) {
                setHasTriggeredValidation(false);
            }
        } catch (error) {
            console.error(error);
        }
    }, [value, validation, isValidated, hasTriggeredValidation]);

    // Trigger validation when parent sets showRequiredError (form submit)
    useEffect(() => {
        if (showRequiredError) {
            setHasTriggeredValidation(true);
        }
    }, [showRequiredError]);

    const fieldValidation = useMemo(() => {
        const response = {
            useValidation: !!validation,
            showValidation: false,
            isValid: true,
        };

        try {
            if (showRequiredError) {
                return response;
            }

            if (!validation) {
                return response;
            }

            if (showRequired && !value) {
                return response;
            }

            // Don't show validation errors for optional empty fields
            if (!value && !required && !showRequired) {
                return response;
            }

            // No input and no validation triggered yet → don't validate
            if (!value && !hasTriggeredValidation) {
                return response;
            }

            response.isValid = value ? validation(`${value}`) : false;

            // Show validation if:
            // 1. Enter was pressed (submit attempt)
            // 2. Validation was previously triggered and still invalid
            // 3. Field was touched (blurred with value)
            if (validateOnEnter && enterIsPressed && !response.isValid) {
                response.showValidation = true;
            } else if (hasTriggeredValidation && !response.isValid) {
                // Keep showing error while typing until valid
                response.showValidation = true;
            } else if (touched && !isFocused) {
                response.showValidation = true;
            }
        } catch (error) {
            console.error(error);
        }
        return response;
    }, [validation, isFocused, value, touched, enterIsPressed, showRequiredError, hasTriggeredValidation]);

    const determineInputMode = () => {
        const inputType = inputMode || (typeof validation === 'string' ? validation : 'text');

        switch (inputType) {
            case 'socialSecurityNumber':
                if (getLanguage() === 'fi') {
                    return 'text';
                }
                return 'numeric';
            case 'text':
                return 'text';
            case 'email':
                return 'email';
            case 'phone':
                return 'tel';
            case 'url':
                return 'url';
            case 'number':
            case 'numeric':
                return 'numeric';
            case 'search':
                return 'search';
            default:
                return 'text';
        }
    };

    const onFieldKeyUp = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (validateOnEnter) {
            if (event.key === 'Enter') {
                setEnterIsPressed(true);
                setHasTriggeredValidation(true);
            } else {
                setEnterIsPressed(false);
            }
        }
    };

    const onFieldChange = (value: string) => {
        if (onChange) {
            onChange(value);
        }
    };

    const onFieldBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const targetElement = event.target as HTMLInputElement | HTMLTextAreaElement;
        setTouched(!!targetElement.value);
        setIsFocused(false);

        // Trigger validation on blur if there's a value
        if (targetElement.value && validation) {
            setHasTriggeredValidation(true);
        }

        if (onBlur) {
            onBlur(targetElement.value);
        }
    };

    // Error state: either from parent (showRequiredError) or field validation
    const hasError = showRequiredError || (fieldValidation.showValidation && !fieldValidation.isValid);

    // When showing info or error, the field needs rounded bottom corners
    const isExpanded = (showInfo && infoText) || (hasError && resolvedErrorMessage);

    return (
        <div
            className={isExpanded ? '' : 'r-space-y-1'}
            data-field-expanded={isExpanded ? 'true' : undefined}
            data-field-grouped={grouped || undefined}
        >
            <label
                htmlFor={htmlName}
                className={clsx('r-relative r-grid r-w-full r-grid-cols-input r-grid-rows-input r-transition-all r-duration-300', {
                    'r-rounded-xl': !grouped,
                    'r-rounded-t-xl': grouped === 'top',
                    'r-rounded-b-xl': grouped === 'bottom' || (grouped && isExpanded),
                    'r-z-10': isFocused,
                    // Outline only for non-grouped fields
                    'r-outline r-outline-2': !grouped,
                    'r-outline-red-700': !grouped && hasError,
                    'r-outline-focus-blue': !grouped && isFocused && !hasError,
                    'r-outline-primary-200': !grouped && !isFocused && !hasError && isActive,
                    'r-outline-gray-400': !grouped && !isFocused && !hasError && !isActive,
                })}
            >
                <div
                    className={clsx(
                        'r-col-auto r-row-start-2 r-flex r-border r-border-transparent r-transition-all r-duration-300 empty:r-hidden',
                        {
                            'r-rounded-xl': !grouped,
                            'r-border-b-transparent':
                                (grouped === 'middle' && !isFocused && !isExpanded) || (grouped === 'top' && !isFocused && !isExpanded),
                            'r-rounded-t-xl': grouped === 'top',
                            'r-rounded-b-xl': grouped === 'bottom' || (grouped && isExpanded),
                            'r-text-gray-600': disabled,
                            '!r-border-red-700': isFocused && hasError,
                            // Border for non-grouped fields (only when focused)
                            'r-border-focus-blue': !grouped && isFocused && !hasError,
                            // Outline for grouped fields
                            'r-outline r-outline-2': grouped,
                            'r-outline-focus-blue': grouped && isFocused && !hasError,
                            'r-outline-red-700': grouped && hasError,
                            'r-outline-gray-400': grouped && !isFocused && !hasError,
                        },
                        disabled ? 'r-bg-gray-100' : hasError ? 'r-bg-red-100' : 'r-bg-white'
                    )}
                >
                    {type === 'textarea' ? (
                        <textarea
                            tabIndex={0}
                            id={htmlName}
                            className={clsx(
                                'custom-focus-indicator r-block r-w-full r-resize-none r-rounded-xl r-border-0 r-px-4 r-pb-1 r-pt-6',
                                disabled ? 'r-bg-gray-100' : hasError ? 'r-bg-red-100' : 'r-bg-white'
                            )}
                            name={htmlName}
                            required={required}
                            aria-required={required || showRequired}
                            placeholder={placeholder}
                            title={infoText}
                            maxLength={maxLength}
                            disabled={disabled}
                            inputMode={determineInputMode()}
                            autoComplete="off"
                            onChange={({ target }) => onFieldChange(target.value)}
                            onFocus={() => setIsFocused(true)}
                            onBlur={onFieldBlur}
                            onKeyUp={onFieldKeyUp}
                            rows={rows}
                            value={value}
                            aria-describedby={
                                showRequiredError || (fieldValidation.showValidation && !fieldValidation.isValid) ? `${htmlName}-error` : undefined
                            }
                            {...((showRequiredError || (fieldValidation.showValidation && !fieldValidation.isValid)) && { 'aria-invalid': true })}
                        />
                    ) : (
                        <div className="r-relative r-w-full">
                            <input
                                tabIndex={0}
                                id={htmlName}
                                ref={fieldRef}
                                className={clsx(
                                    'custom-focus-indicator r-block r-w-full r-rounded-xl r-border-0 r-bg-transparent r-px-4 r-pb-1 r-pt-6 disabled:r-bg-gray-100',
                                    inputClassName
                                )}
                                name={htmlName}
                                required={required}
                                aria-required={required || showRequired}
                                placeholder={placeholder}
                                title={infoText}
                                maxLength={maxLength}
                                type={type}
                                disabled={disabled}
                                inputMode={determineInputMode()}
                                autoComplete="off"
                                onChange={({ target }) => onFieldChange(target.value)}
                                onBlur={onFieldBlur}
                                value={value}
                                min={min}
                                max={max}
                                onKeyUp={onFieldKeyUp}
                                onFocus={() => setIsFocused(true)}
                                aria-describedby={
                                    showRequiredError || (fieldValidation.showValidation && !fieldValidation.isValid)
                                        ? `${htmlName}-error`
                                        : undefined
                                }
                                {...((showRequiredError || (fieldValidation.showValidation && !fieldValidation.isValid)) && { 'aria-invalid': true })}
                            />
                            {suffix && <span className="r-absolute r-inset-y-0 r-right-3 r-flex r-items-center r-text-gray-500">{suffix}</span>}
                        </div>
                    )}

                    {fieldValidation.useValidation && fieldValidation.showValidation && fieldValidation.isValid ? (
                        <span className="r-w-6 r-items-start r-pr-2 r-pt-[1.1rem]">
                            <img src="/assets/icons/field-checkmark.svg" alt="" aria-hidden="true" />
                        </span>
                    ) : null}

                    {infoText && (
                        <button
                            type="button"
                            aria-label={localize('info-button-label')}
                            aria-expanded={showInfo}
                            className="r-grid r-w-[2.5rem] r-cursor-pointer r-place-content-center r-rounded r-border-2 r-border-transparent r-bg-transparent"
                            onClick={() => setShowInfo(!showInfo)}
                            onKeyDown={e => {
                                if (e.key === 'Escape' && showInfo) {
                                    setShowInfo(false);
                                }
                            }}
                        >
                            <img className="r-w-[18px]" src="/assets/icons/field-help.svg" alt="" aria-hidden="true" />
                        </button>
                    )}
                </div>
                <span
                    onClick={() => fieldRef?.current?.focus()}
                    className={clsx('r-absolute r-left-4 r-h-4 r-whitespace-nowrap r-leading-none r-text-gray-500 r-transition-all', {
                        'r-top-2 r-text-xs': isFocused || !!value,
                        'r-text-md r-top-5': !value && !isFocused,
                    })}
                >
                    {label || ''} {required || showRequired ? '*' : ''}
                </span>
            </label>
            {isExpanded && (
                <div
                    className={clsx('r-ml-0.5 r-mt-1.5 r-space-y-1', {
                        'r-mb-4': grouped && grouped !== 'bottom',
                    })}
                >
                    {showInfo && infoText && <InfoMessage message={infoText} darkMode={darkMode} />}
                    {resolvedErrorMessage && hasError && <ErrorMessage id={`${htmlName}-error`} message={resolvedErrorMessage} darkMode={darkMode} />}
                </div>
            )}
        </div>
    );
};
